"use client";

import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useSetAtom } from "jotai";
import { Plus, Trash2 } from "lucide-react";
import type { Edge } from "@xyflow/react";

import { BindableInput } from "./BindableInput";
import { Button } from "@/components/shadcn/button";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { edgesAtom } from "@/store/editor-v2";
import { cn } from "@/lib/utils";

function newId(): string {
  return crypto.randomUUID();
}

interface MergePathRow {
  id: string;
  value: string;
}

/**
 * V2 renderer for `display=ConfigBindableMergePaths` (Merge Paths' `paths`).
 * Each row is one execution path: a stable `id` (which is also the node's
 * per-row input handle on the canvas) and a bindable `value` — usually a
 * whole `:var[...]` ref to the branch's output. Rows can be added/removed;
 * removing a row also drops the edge attached to its handle.
 *
 * Legacy nodes store fixed `path_a`/`path_b` config fields instead of rows —
 * seed the editor from those so the values show up, using the legacy field
 * names as row ids (they match the node's saved edge handles).
 */
export function BindableMergePathsField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  isRequired,
  showOptionalLabel,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  label?: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const legacyA = form.watch(`${nodeId}.path_a`);
  const legacyB = form.watch(`${nodeId}.path_b`);
  const setEdges = useSetAtom(edgesAtom);

  const rows: MergePathRow[] = useMemo(() => {
    if (Array.isArray(raw)) {
      const parsed = raw
        .filter(
          (row): row is Record<string, unknown> =>
            !!row && typeof row === "object"
        )
        .map((row) => ({
          id: typeof row.id === "string" ? row.id : "",
          value: typeof row.value === "string" ? row.value : "",
        }));
      if (parsed.some((row) => row.id || row.value)) return parsed;
    }
    // Legacy config (or a pristine default): seed from path_a/path_b so the
    // saved values render. Row ids reuse the legacy handle names, so the
    // node's existing edges keep pointing at the right rows.
    return [
      { id: "path_a", value: typeof legacyA === "string" ? legacyA : "" },
      { id: "path_b", value: typeof legacyB === "string" ? legacyB : "" },
    ];
  }, [raw, legacyA, legacyB]);

  const write = useCallback(
    (next: MergePathRow[]) =>
      form.setValue(path, next, { shouldDirty: true, shouldTouch: true }),
    [form, path]
  );

  // Backfill ids on rows that were authored without one (e.g. via the API) so
  // every row has a handle. Mirrors BindableRoutesField's id normalization.
  useEffect(() => {
    if (!Array.isArray(raw) || rows.length === 0) return;
    if (rows.every((row) => row.id)) return;
    form.setValue(
      path,
      rows.map((row) => (row.id ? row : { ...row, id: newId() })),
      { shouldDirty: false, shouldTouch: false }
    );
  }, [raw, rows, form, path]);

  const setRowValue = (index: number, value: string) => {
    const next = rows.slice();
    next[index] = { ...next[index], value };
    write(next);
  };

  const addRow = () => {
    write([...rows, { id: newId(), value: "" }]);
  };

  const removeRow = (index: number) => {
    const removed = rows[index];
    write(rows.filter((_, rowIndex) => rowIndex !== index));
    if (removed?.id) {
      setEdges((prev: Edge[]) =>
        prev.filter(
          (edge) =>
            !(edge.target === nodeId && edge.targetHandle === removed.id)
        )
      );
    }
  };

  const canDelete = rows.length > 2;

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          name={fieldKey}
          noMargin
          isDisabled={disabled}
          isRequired={isRequired}
          showOptionalLabel={showOptionalLabel}
        />
      ) : null}
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      <div
        className={cn(
          "flex w-full flex-col gap-3",
          error && "rounded-md p-1.5 ring-1 ring-inset ring-amber-400/70"
        )}
      >
        {rows.map((row, index) => (
          <div
            key={row.id || `index-${index}`}
            className="flex flex-col gap-1.5"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">Path {index + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled || !canDelete}
                title={
                  canDelete ? "Remove path" : "At least two paths are required"
                }
                className="size-8 text-muted-foreground"
                onClick={() => removeRow(index)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
            <BindableInput
              nodeId={nodeId}
              dataType="str"
              value={row.value}
              placeholder="Value carried by this path"
              disabled={disabled}
              inputName={row.id || undefined}
              onChange={(next) => setRowValue(index, next)}
            />
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full"
          onClick={addRow}
        >
          <Plus className="mr-1 size-4" /> Add path
        </Button>
      </div>
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}
